/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnInit,
  inject,
  input,
  signal,
  DestroyRef,
  output,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DIALOG_BODY_CLASS, uniqueId } from '@shared/utils';
import {
  DateRange,
  DomHandlerService,
  Logger,
  convertCzDateToIso,
  convertSimplifiedDate,
  parseStringToDate,
  restrictDatepickerInput,
  simplifyDate,
} from '@shared/utils';
import { DatePicker, DatePickerModule } from '@openng/optimus-ui/datepicker';
import { DatepickerClearIconFixDirective } from '../directives/datepicker-clear-icon-fix.directive';
import { NEVER, merge } from 'rxjs';
import { BaseFormControlDirective, BaseFormControlDirectiveInputs } from '../directives/base-form-control.directive';
import {
  DatePickerFormControlDirective,
  DatePickerFormControlDirectiveInputs,
  DatePickerFormControlDirectiveOutputs,
} from '../directives/datepicker-form-control.directive';
import {
  DropdownFormControlDirective,
  DropdownFormControlDirectiveInputs,
} from '../directives/dropdown-form-control.directive';
import { dateFromGreaterThanToValidator, datepickerRangeBoundsValidator } from './validators';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { DateType } from '../datepicker/datepicker.component';
import { FormComponent } from '../form/form.component';
import { FormConnectorService } from '../forms.utils';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';

const CALENDAR_OPEN_HEIGHT_PX = 490;

export type DateRangeFormControl = FormControl & {
  fromKey: string;
  toKey: string;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ui-datepicker-range',
  // @container: při šířce pole pod 22rem (dvě data vedle sebe se nevejdou) jsou pole od–do pod sebou
  host: { class: 'block mb-4 @container' },
  templateUrl: './datepicker-range.component.html',
  imports: [
    CommonModule,
    DatePickerModule,
    DatepickerClearIconFixDirective,
    ReactiveFormsModule,
    InputWrapperComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatepickerRangeComponent,
      multi: true,
    },
  ],
  hostDirectives: [
    {
      directive: BaseFormControlDirective,
      inputs: BaseFormControlDirectiveInputs,
    },
    {
      directive: DropdownFormControlDirective,
      inputs: DropdownFormControlDirectiveInputs,
    },
    {
      directive: DatePickerFormControlDirective,
      inputs: DatePickerFormControlDirectiveInputs,
      outputs: DatePickerFormControlDirectiveOutputs,
    },
  ],
})
export class DatepickerRangeComponent extends BaseControlValueAccessor<DateRange> implements AfterViewInit, OnInit {
  protected _form = inject(FormComponent, { optional: true, skipSelf: true });
  protected _formConnectorService = inject(FormConnectorService, { optional: true, skipSelf: true });

  private readonly destroyRef = inject(DestroyRef);

  placeholderFrom = input<string>('Vyberte');
  placeholderTo = input<string>('Vyberte');

  readonly handleChange = output<DateRange>();

  readonly calendarRefFrom = viewChild<DatePicker>('calendarRefFrom');
  readonly calendarRefTo = viewChild<DatePicker>('calendarRefTo');

  restrictInput = restrictDatepickerInput;
  protected submitted = signal<boolean>(false);
  randomInputId = uniqueId();
  // signál: pozice se mění i v setTimeout, kde by se bez zone.js nevykreslila
  readonly panelStyle = signal<Record<string, string>>({});

  private _el = inject(ElementRef);
  private _dom = inject(DomHandlerService);

  override logger: Logger = this.loggerService.createLogger('DatepickerRangeComponent');

  base = inject(BaseFormControlDirective);
  dropdown = inject(DropdownFormControlDirective);
  datePicker = inject(DatePickerFormControlDirective);

  DateRangeDefaultValue: DateRange = { [this.datePicker.fromKey()]: null, [this.datePicker.toKey()]: null };
  // Vytvoření FormGroup pro innerControlFrom a innerControlTo
  innerFG = new FormGroup({
    [this.datePicker.fromKey()]: new FormControl<string | Date | null>(null),
    [this.datePicker.toKey()]: new FormControl<string | Date | null>(null),
  });

  constructor() {
    const injector = inject(Injector);

    super(injector);
    merge<boolean[]>(this._formConnectorService?.submitted$ ?? NEVER, this._form?.getSubmitted() ?? NEVER)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        // this.cdRef.detectChanges();
        this.submitted.set(_);
      });

    this.value.set(this.DateRangeDefaultValue);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    // Nastavení validátoru pro kontrolu rozsahu dat
    this.control.addValidators([
      dateFromGreaterThanToValidator(),
      datepickerRangeBoundsValidator(this.datePicker.minDate(), this.datePicker.maxDate()),
    ]);
    // Validátor by měl zareagovat na změny hodnot
    this.control.updateValueAndValidity();

    (this.control as DateRangeFormControl).fromKey = this.datePicker.fromKey();
    (this.control as DateRangeFormControl).toKey = this.datePicker.toKey();
  }

  ngAfterViewInit(): void {
    this.innerFG.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      switch (this.datePicker.dataType()) {
        case DateType.Date: {
          this.value.set(value);
          break;
        }
        case DateType.DateAsString: {
          this.value.set({
            [this.datePicker.fromKey()]: value[this.datePicker.fromKey()]
              ? simplifyDate(value[this.datePicker.fromKey()] as Date)
              : null,
            [this.datePicker.toKey()]: value[this.datePicker.toKey()]
              ? simplifyDate(value[this.datePicker.toKey()] as Date)
              : null,
          });
          break;
        }
      }
      this.logger.debug('valueChanges>>', value, this.value());
      this.onChange(this.value());
    });

    // Nastavení minDate a maxDate při inicializaci komponenty
    this.updateMinDateTo();
    this.updateMaxDateFrom();

    // Reakce na změny v "od" datu
    this.innerFG
      .get(this.datePicker.fromKey())
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateMinDateTo(); // Aktualizuje pouze minDate pro kalendář "do"
      });

    // Reakce na změny v "do" datu
    this.innerFG
      .get(this.datePicker.toKey())
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateMaxDateFrom(); // Aktualizuje pouze maxDate pro kalendář "od"
      });

    // Add paste event listeners to both datepicker inputs
    const calendarRefFrom = this.calendarRefFrom();
    if (calendarRefFrom?.inputfieldViewChild) {
      (calendarRefFrom.inputfieldViewChild.nativeElement as HTMLInputElement).addEventListener('paste', (event) =>
        this.handlePasteEvent(event, this.datePicker.fromKey())
      );
    }

    const calendarRefTo = this.calendarRefTo();
    if (calendarRefTo?.inputfieldViewChild) {
      (calendarRefTo.inputfieldViewChild.nativeElement as HTMLInputElement).addEventListener('paste', (event) =>
        this.handlePasteEvent(event, this.datePicker.toKey())
      );
    }
  }

  handleOnEnter(event: any, key: string) {
    this.processInsertExplicitDate(event.target.value, key);
  }

  handlePasteEvent(event: ClipboardEvent, key: string) {
    setTimeout(() => {
      const input = event.target as HTMLInputElement;
      this.processInsertExplicitDate(input.value, key);
    }, 0);
  }

  get isInDialog() {
    const bodyEl = this._dom.bodyEl;
    return bodyEl.classList.contains(DIALOG_BODY_CLASS);
  }

  handleOnMonthChange() {
    const scrollTop = this._dom.htmlEl.scrollTop || 0;

    setTimeout(() => {
      if (this.isInDialog) this._dom.htmlEl.scrollTop = scrollTop;
    });

    setTimeout(() => {
      this._setTopPosition(1);
    }, 50);
  }

  // For datepickers in dialogs, we need to handle the top position differently
  setCorrectCalendarTopPosition() {
    this._setTopPosition(0);
    // TODO: The 'emit' function requires a mandatory null argument
    this.datePicker.handleShow.emit();
  }

  handleOnClose() {
    this.panelStyle.set({});
  }

  // #region -------------------------------------------------------- ControlValueAccessor --------------------------------------------------------

  // Prázdné funkce, které se zaregistrují pro změny hodnoty a dotyk
  // ted se nepozivaji, je reseno pomoci formControlDirective
  // onChange: (value: DateRange | null) => void = noop;
  // onTouched: () => void = noop;

  // Tato metoda je volána API formulářů pro zápis do zobrazení, když jsou požadovány programové změny z modelu do zobrazení
  override writeValue(value: DateRange): void {
    // Pokud je hodnota null, nastav výchozí hodnotu na DateRangeDefaultValue
    const newValue = value ?? this.DateRangeDefaultValue;
    this.value.set(newValue);
    switch (this.datePicker.dataType()) {
      case DateType.Date: {
        this.innerFG.setValue(
          {
            [this.datePicker.fromKey()]: newValue[this.datePicker.fromKey()],
            [this.datePicker.toKey()]: newValue[this.datePicker.toKey()],
          },
          { emitEvent: false }
        );
        break;
      }
      case DateType.DateAsString: {
        this.innerFG.setValue(
          {
            [this.datePicker.fromKey()]: parseStringToDate(newValue[this.datePicker.fromKey()] as string),
            [this.datePicker.toKey()]: parseStringToDate(newValue[this.datePicker.toKey()] as string),
          },
          { emitEvent: false }
        );
        break;
      }
    }
  }
  // #endregion -------------------------------------------------------- ControlValueAccessor --------------------------------------------------------

  // #region -------------------------------------------------------- Private Methods --------------------------------------------------------
  private processInsertExplicitDate(value: string, key: string) {
    let processedValue = value;

    // Zkontrolujeme, jestli je datum ve zjednodušeném formátu (ddMMyy)
    const simplifiedDate = convertSimplifiedDate(processedValue);
    if (simplifiedDate) {
      processedValue = simplifiedDate; // Pokud je, použijeme převedený plný formát
    }

    // Převod z cz formátu na ISO
    const isoDate = convertCzDateToIso(processedValue);
    const date = isoDate ? parseStringToDate(isoDate) : null;

    // Aktualizace hodnoty v rámci formuláře
    this.innerFG.patchValue({ [key]: date ?? null });

    // Informování Angularu, že prvek byl "dotknut"
    this.onTouched();
  }

  private _setTopPosition(diff = 0) {
    if (!this.isInDialog) return;

    const y = (this._el.nativeElement as HTMLElement).getBoundingClientRect().top;
    const inputHeight = (this._el.nativeElement as HTMLElement).getBoundingClientRect().height;
    const panelTop = this._dom.htmlEl.scrollTop + diff;
    if (y < CALENDAR_OPEN_HEIGHT_PX)
      this.panelStyle.set({
        top: `${this.datePicker.timeOnly() ? panelTop + y + inputHeight : panelTop}px`,
      });
  }

  private updateMinDateTo(): void {
    const fromDate = this.innerFG.get(this.datePicker.fromKey())?.value;
    this.calendarRefTo()!.minDate = fromDate ? new Date(fromDate) : null;
  }

  private updateMaxDateFrom(): void {
    const toDate = this.innerFG.get(this.datePicker.toKey())?.value;
    this.calendarRefFrom()!.maxDate = toDate ? new Date(toDate) : null;
  }
  //#endregion ------------------------------------------------------ Private Methods --------------------------------------------------------
}
